import { useEffect, useMemo } from 'react';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import type { WritableAtom } from 'nanostores';
import type { AppBundle } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { accountStore } from '@/services/auth/store';
import { useActiveBundle } from '@/context/active-bundle';

/**
 * Derive a stable depth from a breadcrumb's `href` by counting URL
 * path segments. A parent route's href has fewer segments than its
 * child's, so depth-by-segment-count yields parent-first ordering
 * automatically — without needing a router context (works in tests),
 * survives in-tree navigation (the parent's effect doesn't have to
 * re-run), and has no dependency on render or effect order.
 *
 * Non-path hrefs — most commonly `'#'`, used as a sentinel for the
 * "current page, not clickable" crumb — sort to the *end*. The
 * convention in this codebase is to put the unclickable crumb last
 * in the registration array, and stable sort keeps it there relative
 * to its sibling group.
 *
 * Why this matters: React's effect order is bottom-up, so a child
 * page's `useEffect` runs *before* its parent layout's. Pure
 * append-order puts the child crumb first. Sorting by href-depth at
 * read time recovers the route-hierarchy order regardless of which
 * component's effect ran first.
 */
function depthForHref(href: string): number {
  // Strip search/hash so the path comparison is just the URL segments.
  const path = href.split('?')[0].split('#')[0];
  // Non-path hrefs (`'#'`, empty, `'mailto:'`, etc.) aren't part of the
  // route hierarchy — they're the "current page" sentinel. Send them to
  // the end so they don't outrank actual route hrefs that may have zero
  // segments (rare, but defensively handled).
  if (path === '' || !path.startsWith('/')) return Number.MAX_SAFE_INTEGER;
  return path.split('/').filter(Boolean).length;
}

function sortedByDepth(items: ReadonlyArray<BreadcrumbItem>): BreadcrumbItem[] {
  // Stable sort: ES2019 Array.prototype.sort is stable, so same-depth
  // items keep their insertion order — important for the tie-breaker
  // case of two crumbs registered with the same href depth (e.g. two
  // `'#'`-href crumbs in one registration array).
  return [...items].sort((a, b) => depthForHref(a.href) - depthForHref(b.href));
}
export interface BreadcrumbItem {
  /**
   * A unique identifier for the breadcrumb item.
   * Typically this would be a unique id for a route, or a path identifier with dynamic
   * params.
   */
  id: string;
  /**
   * The name to show for the breadcrumb item.
   * This is what will be shown in the UI.
   * @deprecated Use `text` instead
   */
  name?: string;
  /**
   * The name to show for the breadcrumb item.
   * This is what will be shown in the UI.
   */
  text: string;
  /**
   * What to use for this crumb in the *document title* instead of `text`.
   *
   * `document.title` escapes the DOM: it is attached to every analytics event
   * (`getAnalyticsMetaProperties`) and passed to Copilot as LLM context, and
   * `fs-mask` cannot follow it there. A crumb whose `text` is masked because it
   * carries customer content (a rendered subject, a person's name) should set a
   * non-sensitive stand-in here — usually an id — so the tab stays
   * distinguishable without exporting the content.
   *
   * Only affects the title; the crumb still renders `text`.
   */
  titleText?: string;
  /**
   * The href to link to for this breadcrumb item. This should be a correct
   * URL with all its dynamic params already resolved.
   */
  href: string;
  /**
   * Optional additional class names to apply to the breadcrumb item.
   * This can be used to apply `fs-mask` classes to hide sensitive information
   * in FullStory sessions.
   */
  className?: string;
  /**
   * Whether this breadcrumb should be truncated with a max length.
   * This should be used for breadcrumbs containing names based on
   * dynamic values, like user-defined names.
   */
  isTruncated?: boolean;
}

export const breadcrumbsStores = atom<Map<'root' | AppBundle, WritableAtom<BreadcrumbItem[]>>>(new Map());

function getBundleBreadcrumbsStore(bundle: 'root' | AppBundle) {
  const stores = breadcrumbsStores.get();

  if (stores.has(bundle)) {
    return stores.get(bundle)!;
  }

  const newStore = atom<BreadcrumbItem[]>([]);
  const newStores = new Map(stores);
  newStores.set(bundle, newStore);
  breadcrumbsStores.set(newStores);
  return newStore;
}

function getCurrentUrlBreadcrumbsStore() {
  const app = getAppBundleFromUrl(window.location.pathname, accountStore.get());
  return getBundleBreadcrumbsStore(app ?? 'root');
}

export function getBreadcrumbsStore(bundle?: 'root' | AppBundle) {
  if (bundle != null) {
    return getBundleBreadcrumbsStore(bundle);
  }

  return getCurrentUrlBreadcrumbsStore();
}

export function append(item: BreadcrumbItem, bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);
  const previous = store.get();
  if (previous.some((existing) => existing.id === item.id)) return;
  store.set(sortedByDepth([...previous, item]));
}

export function rollback(id: string, bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);
  store.set(store.get().filter((existing) => existing.id !== id));
}

export function replace(item: BreadcrumbItem, bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);

  const previous = store.get();
  const index = previous.findIndex((existing) => existing.id === item.id);
  if (index === -1) {
    return;
  }
  const next = [...previous];
  next[index] = item;
  // Re-sort: if the replacement's `href` has a different segment count
  // than the original, the depth invariant `append` established would
  // otherwise be silently broken (e.g. replacing a depth-3 crumb with
  // a depth-5 one would leave it sitting in the wrong slot).
  store.set(sortedByDepth(next));
}

export function getLastBreadcrumb(bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);
  const breadcrumbs = store.get();
  return breadcrumbs.at(-1);
}

/**
 * The crumb chain as a title, leaf first — Ember's `crumbs.createTitle()`
 * ordering, without the trailing `| Customer.io`. Crumbs carrying customer
 * content substitute their {@link BreadcrumbItem.titleText} stand-in, so the
 * title never exports what the rendered crumb masks.
 */
function titleChainOf(breadcrumbs: ReadonlyArray<BreadcrumbItem>) {
  const names = breadcrumbs.map((breadcrumb) => breadcrumb.titleText ?? breadcrumb.text ?? breadcrumb.name);
  return [...names].reverse().join(' | ');
}

export function getTitle(bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);

  const breadcrumbs = store.get();
  if (breadcrumbs.length === 0) {
    return 'Customer.io';
  }
  return `${titleChainOf(breadcrumbs)} | Customer.io`;
}

export type WindowHydraBreadcrumbs = {
  breadcrumbsStores: typeof breadcrumbsStores;
  append: typeof append;
  rollback: typeof rollback;
  replace: typeof replace;
  getTitle: typeof getTitle;
  getLastBreadcrumb: typeof getLastBreadcrumb;
};

Object.assign(window, {
  __hydra_breadcrumbs__: {
    breadcrumbsStores,
    append,
    rollback,
    replace,
    getTitle,
    getLastBreadcrumb,
  } satisfies WindowHydraBreadcrumbs,
});

export function useBreadcrumbs() {
  // Show breadcrumbs for the currently active app.
  // Apps might get suspended/hidden when navigating between routes,
  // but they don't get destroyed, so they wouldn't call "rollback" on their
  // breadcrumbs when they get hidden.
  // This prevents showing breadcrumbs from such hidden apps.
  const activeApp = useActiveBundle();
  // `activeApp` is derived from the router's location. A `null` value means the
  // current route is Hydra-owned, and Hydra-registered breadcrumbs always live
  // in the 'root' store. Mapping `null` to 'root' here keeps the read side on
  // the same source of truth as `activeApp`. Falling back to `undefined` (and
  // thus re-deriving the bundle from `window.location` inside
  // `getCurrentUrlBreadcrumbsStore`) races mid-navigation: `window.location`
  // can still read the previous URL after `activeApp` has updated, resolving to
  // the wrong bundle store and dropping the breadcrumbs.
  const breadcrumbsStore = useMemo(() => getBreadcrumbsStore(activeApp ?? 'root'), [activeApp]);
  const breadcrumbs = useStore(breadcrumbsStore);
  return { breadcrumbs, append, rollback, replace, getTitle, getLastBreadcrumb };
}

/**
 * The registered crumb chain as a document title, leaf first — the reactive
 * counterpart to {@link getTitle} (which Ember's `crumbs.createTitle()` calls
 * through the `window.__hydra_breadcrumbs__` bridge). Reading the store rather
 * than a locally-held array means the tab title and the rendered crumb trail
 * can't drift: it picks up crumbs registered by ancestors and the store's
 * depth-based re-sort.
 *
 * Suffix-free, so it can be handed to `DocumentTitle`, which appends
 * `| Customer.io` itself. Empty on the first render, before
 * {@link useRegisterBreadcrumbs}' effect has appended anything — callers should
 * skip rendering the title until it has content.
 */
export function useBreadcrumbTitle() {
  const { breadcrumbs } = useBreadcrumbs();
  return titleChainOf(breadcrumbs);
}

export function useRegisterBreadcrumbs(breadcrumbs: BreadcrumbItem[]) {
  // Construct a unique string from the breadcrumbs to run the effect when any of them changes.
  // We don't use the array itself as a dependency in case it's created on each render, which would
  // always give us a unique array, making the effect always run.
  const breadcrumbsKey = useMemo(
    () =>
      breadcrumbs
        .map((breadcrumb) => `${breadcrumb.id}_${breadcrumb.href}_${breadcrumb.text}_${breadcrumb.name}`)
        .join(' '),
    [breadcrumbs],
  );

  useEffect(
    function registerBreadcrumbs() {
      // Order isn't a function of *when* this effect runs — the store
      // re-sorts on every insert by each crumb's href-segment depth.
      // So a layout that registers `[Snippets]` and a child that
      // registers `[New]` always end up `Snippets / New` regardless of
      // mount order (initial load OR in-tree navigation).
      for (const breadcrumb of breadcrumbs) {
        append(breadcrumb, 'root');
      }

      return () => {
        for (const breadcrumb of breadcrumbs) {
          rollback(breadcrumb.id, 'root');
        }
      };
    },
    // oxlint-disable-next-line react/exhaustive-deps -- complains about breadcrumbs not present but we construct a special string for them
    [breadcrumbsKey],
  );
}
